import React, { useEffect, useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { api } from "@/lib/api";
import { formatDate, formatDateShort } from "@/lib/dateFormat";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useHelp } from "@/context/HelpContext";
import QuotaCalendar from "@/components/QuotaCalendar";
import ProfilePanel from "@/components/ProfilePanel";
import { UtilityForecastPanel } from "@/components/UtilityForecastPanel";
import { toast } from "sonner";
import {
  Droplets,
  TrendingUp,
  Users,
  Factory,
  Waves,
  Filter,
  LineChart as LineIcon,
} from "lucide-react";
import {
  AreaChart,
  Area,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export default function UtilityDashboard() {
  const { t } = useI18n();
  const { user, refreshMe } = useAuth();
  const { setHelpKey } = useHelp();
  const [summary, setSummary] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [large, setLarge] = useState([]);
  const [range, setRange] = useState({ quota_m3: 0, usage: {} });
  const [forecast, setForecast] = useState(null);
  const [allMunis, setAllMunis] = useState([]);
  const [scopeDraft, setScopeDraft] = useState(user?.served_municipality_ids || []);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    setHelpKey(`util.${tab}`);
    return () => setHelpKey(null);
  }, [tab, setHelpKey]);

  const pending = user && user.role === "utility" && !user.approved;

  const todayStr = new Date().toISOString().slice(0, 10);

  const load = async () => {
    if (pending) {
      // SEC-001: unapproved utility fetches no data — but still needs
      // /municipalities so the pending panel can resolve names.
      try {
        const r = await api.get("/municipalities");
        setAllMunis(r.data || []);
      } catch {
        /* ignore */
      }
      return;
    }
    const [s, b, l, r, f, ms] = await Promise.all([
      api.get("/utility/summary"),
      api.get("/bookings"),
      api.get("/large-consumer-requests"),
      api.get(
        `/quota/range?start=${todayStr}&end=${new Date(
          Date.now() + 13 * 864e5,
        ).toISOString().slice(0, 10)}`,
      ),
      api.get("/utility/forecast?days=30"),
      api.get("/municipalities"),
    ]);
    setSummary(s.data);
    setBookings(b.data);
    setLarge(l.data);
    setRange(r.data);
    setForecast(f.data);
    setAllMunis(ms.data);
  };

  const muniName = (id) => {
    const m = allMunis.find((mm) => mm.id === id);
    return m?.name || id;
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setScopeDraft(user?.served_municipality_ids || []);
  }, [user]);

  const toggleMuni = (id) => {
    setScopeDraft((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const saveScope = async () => {
    await api.patch("/auth/preferences", { served_municipality_ids: scopeDraft });
    await refreshMe();
    toast.success(t("save") + " ✓");
    await load();
  };

  const rangeChartData = Object.entries(range.usage || {})
    .sort()
    .map(([d, v]) => ({ d: formatDateShort(d), used: v }));

  const scopeLabel = () => {
    if (!scopeDraft || scopeDraft.length === 0) return t("scope_all");
    if (scopeDraft.length === 1) {
      const m = allMunis.find((mm) => mm.id === scopeDraft[0]);
      return m?.name || `1 ${t("municipality")}`;
    }
    return `${scopeDraft.length} ${t("municipality")}`;
  };

  return (
    <div className="space-y-6">
      {pending && (
        <Card className="border-primary/40 bg-primary/[0.04]" data-testid="utility-pending-panel">
          <CardContent className="p-6">
            <div className="label-caps mb-2 text-primary">Water Planner</div>
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {t("utility_pending_title")}
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              {t("utility_pending_body")}
            </p>
            {user?.requested_municipality_ids?.length > 0 && (
              <div className="mt-4">
                <div className="label-caps mb-2">{t("utility_requested_scope")}</div>
                <ul
                  className="flex flex-wrap gap-2"
                  data-testid="utility-pending-requested-list"
                >
                  {user.requested_municipality_ids.map((id) => (
                    <li
                      key={id}
                      className="rounded-md border border-border bg-background px-3 py-1 text-xs"
                    >
                      {muniName(id)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(!user?.requested_municipality_ids ||
              user.requested_municipality_ids.length === 0) && (
              <p className="mt-4 text-sm text-muted-foreground">
                {t("utility_pending_no_data")}
              </p>
            )}
          </CardContent>
        </Card>
      )}
      {!pending && (
        <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-caps">{t("utility")}</div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            {t("utility_dashboard")}
          </h1>
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="scope-picker-btn">
              <Filter className="h-4 w-4" />
              <span>{t("scope")}:</span>
              <span className="font-mono-jb text-sm">{scopeLabel()}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-3" data-testid="scope-picker-popover">
            <div className="label-caps mb-2">{t("all_municipalities")}</div>
            <ul className="max-h-64 space-y-1 overflow-auto">
              {allMunis.map((m) => (
                <li key={m.id}>
                  <label
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                  >
                    <Checkbox
                      checked={scopeDraft.includes(m.id)}
                      onCheckedChange={() => toggleMuni(m.id)}
                      data-testid={`scope-check-${m.id}`}
                    />
                    <span className="flex-1">{m.name}</span>
                    <span className="font-mono-jb text-xs text-muted-foreground">
                      {m.daily_quota_m3} m³
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex justify-between gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setScopeDraft([])}
                data-testid="scope-clear-btn"
              >
                {t("scope_all")}
              </Button>
              <Button size="sm" onClick={saveScope} data-testid="scope-save-btn">
                {t("save_scope")}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      )}

      {!pending && (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Kpi icon={Droplets} label={t("total_available")} value={summary ? String(summary.total_available_m3) : "—"} unit="m³" testId="util-kpi-available" accent />
        <Kpi icon={TrendingUp} label={t("total_used")} value={summary ? String(summary.total_used_today_m3) : "—"} unit={`/ ${summary?.total_quota_m3 ?? "—"} m³`} testId="util-kpi-used" />
        <Kpi icon={Users} label={t("citizens")} value={summary ? String(summary.pool_owner_count) : "—"} unit="" testId="util-kpi-pool-owners" />
        <Kpi icon={Factory} label={t("large_consumer")} value={summary ? String(summary.large_pending) : "—"} unit={t("pending").toLowerCase()} testId="util-kpi-large" />
      </div>
      )}

      {!pending && (
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="util-tabs" className="max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview" data-testid="util-tab-overview">{t("dashboard")}</TabsTrigger>
          <TabsTrigger value="forecast" data-testid="util-tab-forecast">{t("forecast")}</TabsTrigger>
          <TabsTrigger value="munis" data-testid="util-tab-munis">{t("all_municipalities")}</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="util-tab-bookings">{t("all_bookings")}</TabsTrigger>
          <TabsTrigger value="large" data-testid="util-tab-large">{t("large_consumer")}</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="util-tab-calendar">{t("calendar")}</TabsTrigger>
          <TabsTrigger value="profile" data-testid="util-tab-profile">Profil</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <ProfilePanel />
        </TabsContent>

        <TabsContent value="overview">
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <Waves className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">{t("demand_curve")} (14d)</h3>
              </div>
              {rangeChartData.length === 0 ? (
                <div className="grid h-64 place-items-center text-sm text-muted-foreground">
                  {t("no_data")}
                </div>
              ) : (
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <AreaChart data={rangeChartData}>
                      <defs>
                        <linearGradient id="waveFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#00A6FB" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#00A6FB" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                      <XAxis dataKey="d" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                      <Area type="monotone" dataKey="used" stroke="#00A6FB" strokeWidth={2} fill="url(#waveFill)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="forecast">
          <UtilityForecastPanel forecast={forecast} t={t} />
        </TabsContent>

        <TabsContent value="munis">
          <Card className="border-border">
            <CardContent className="p-6">
              <h3 className="mb-4 text-lg font-semibold">{t("all_municipalities")}</h3>
              {summary?.municipalities?.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-border label-caps text-left">
                        <th className="py-2 pr-3">{t("municipality")}</th>
                        <th className="py-2 pr-3">{t("total_quota")}</th>
                        <th className="py-2 pr-3">{t("used_today")}</th>
                        <th className="py-2 pr-3">{t("available_today")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.municipalities.map((m) => (
                        <tr key={m.id} className="border-b border-border/60">
                          <td className="py-2 pr-3 font-medium">{m.name}</td>
                          <td className="py-2 pr-3 font-mono-jb">{m.daily_quota_m3} m³</td>
                          <td className="py-2 pr-3 font-mono-jb">{m.used_today_m3} m³</td>
                          <td className={`py-2 pr-3 font-mono-jb ${m.available_m3 <= 0 ? "text-destructive" : "text-primary"}`}>
                            {m.available_m3} m³
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">{t("no_data")}</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bookings">
          <Card className="border-border">
            <CardContent className="p-6">
              <h3 className="mb-4 text-lg font-semibold">{t("all_bookings")}</h3>
              {bookings.length === 0 ? (
                <div className="text-sm text-muted-foreground">{t("no_bookings")}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-border label-caps text-left">
                        <th className="py-2 pr-3">{t("date")}</th>
                        <th className="py-2 pr-3">{t("name")}</th>
                        <th className="py-2 pr-3">{t("volume_m3")}</th>
                        <th className="py-2 pr-3">{t("status")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((b) => (
                        <tr key={b.id} className="border-b border-border/60">
                          <td className="py-2 pr-3 font-mono-jb" title={b.date}>{formatDate(b.date)}</td>
                          <td className="py-2 pr-3">{b.user_name}</td>
                          <td className="py-2 pr-3 font-mono-jb">{b.volume_m3}</td>
                          <td className="py-2 pr-3">
                            <Badge variant="outline" className="capitalize">{t(b.status)}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="large">
          <Card className="border-border">
            <CardContent className="p-6">
              <h3 className="mb-4 text-lg font-semibold">{t("large_consumer")}</h3>
              {large.length === 0 ? (
                <div className="text-sm text-muted-foreground">{t("no_data")}</div>
              ) : (
                <ul className="divide-y divide-border">
                  {large.map((r) => (
                    <li key={r.id} className="flex flex-wrap items-center gap-3 py-3">
                      <div className="min-w-[110px] font-mono-jb text-sm" title={r.date}>{formatDate(r.date)}</div>
                      <Badge variant="outline" className="capitalize">{t(r.category)}</Badge>
                      <div className="font-mono-jb text-sm">{r.volume_m3} m³</div>
                      <div className="flex-1 truncate text-sm text-muted-foreground">{r.user_name}: {r.description}</div>
                      <Badge variant="outline" className="capitalize">{t(r.status)}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calendar">
          <QuotaCalendar />
        </TabsContent>
      </Tabs>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, unit, testId, accent }) {
  return (
    <Card className={`border-border ${accent ? "border-primary/40" : ""}`}>
      <CardContent className="p-6">
        <div className="mb-3 flex items-center justify-between">
          <div className="label-caps">{label}</div>
          <Icon className={`h-4 w-4 ${accent ? "text-primary" : "text-muted-foreground"}`} />
        </div>
        <div className="flex items-baseline gap-2" data-testid={testId}>
          <span className={`font-mono-jb text-3xl font-semibold ${accent ? "text-primary" : ""}`}>{value}</span>
          <span className="text-sm text-muted-foreground">{unit}</span>
        </div>
      </CardContent>
    </Card>
  );
}
